import { Box, Typography, useTheme, Button, TextField, Autocomplete } from "@mui/material";
import { useNavigate }  from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState }     from "react";
import moment           from "moment";

import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import CancelIcon        from "@mui/icons-material/Cancel";
import ReceiptIcon       from "@mui/icons-material/Receipt";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListPageHeader from "components/list/ListPageHeader";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import FixedSizeDialog from "components/fixedSizeDialog";
import PurchaseReturnViewForm from "../forms/returns/returnViewForm";
import { currencyFormatter } from "utilities/currencyFormatter";
import { usePurchaseReturnList } from "api/hooks/inventory";
import { requester } from "api/pesabackend";
import useListDateFilter from "hooks/useListDateFilter";

export default function SupplierReturnTable({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg = theme.palette.background.alt;
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    purchaseReturnList, count, isLoadingPurchaseReturnList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange, setNewReturnData,
  } = usePurchaseReturnList(1, business_branch_id);

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const [openRecord, setOpenRecord] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);
  const [viewData, setViewData] = useState({});
  const [clickedItem, setClickedItem] = useState("");
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState("");

  const fetchInvoiceList = async (search) => {
    try {
      const url = `/inventory/product_purchase/list?business_branch=${business_branch_id}&supplierFilter=all`;
      const res = await requester.get(search ? `${url}&search=${search}` : url);
      setInvoiceList(res.data.data);
    } catch {}
  };

  const handleOpenReturn = async (invoiceId) => {
    const res = await requester.get(`/inventory/product_purchase/get/${invoiceId}`);
    if (res.status === 200) navigate("/supplierReturnForm", { state: { id: invoiceId, data: res.data.data } });
  };

  const handleClickView = async (id) => {
    const res = await requester.get(`/inventory/purchase_return/get_form/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const handleClickCancelOpen = async (id) => {
    const res = await requester.get(`/inventory/purchase_return/get_form/${id}`);
    if (res.status === 200) { setClickedItem(id); setOpenCancel(true); }
  };

  const handleClickDelete = async () => {
    const res = await requester.post(`/inventory/purchase_return/cancel/${clickedItem}`);
    if (res.status === 201) { setIsSuccess(true); setNewReturnData(true); setOpenCancel(false); }
  };

  const columns = [
    { label: "Return Date"              },
    { label: "Purchase Invoice"         },
    { label: "Supplier"                 },
    { label: "Total Return", align: "right" },
    { label: "Created By"               },
    { label: "Date Created"             },
    { label: "Actions",     align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <ListPageHeader icon={<AssignmentReturnIcon sx={{ fontSize: "22px" }} />} title="Supplier Returns" count={count}>
        <Button onClick={() => { fetchInvoiceList(null); setOpenRecord(true); }} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Create Return
        </Button>
      </ListPageHeader>

      <ListFilterBar>
        <ListDateFilterBar embedded dateFilter={displayDateFilter} customRange={customRange}
          onFilterChange={handleFilterChange} onCustomRangeChange={setCustomRange} onCustomApply={handleCustomApply} />
      </ListFilterBar>

      <ReportTable columns={columns} rows={purchaseReturnList} loading={isLoadingPurchaseReturnList}
        hasMore={hasMore} onLoadMore={loadMore} loadingMore={isLoadingMore}
        emptyMessage="No supplier returns found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{moment(row.return_date).format("LL")}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.related_purchase.invoice_number}</Typography></RCell>
            <RCell>{row.related_supplier.name}</RCell>
            <RCell align="right"><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{currencyFormatter(parseFloat(row.grand_total))}</Typography></RCell>
            <RCell>{row.added_by.first_name} {row.added_by.last_name}</RCell>
            <RCell>{moment(row.date_added).format("LLL")}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                <Box onClick={() => handleClickView(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                  <VisibilityIcon sx={{ fontSize: "14px" }} /> View
                </Box>
                <Box onClick={() => handleClickCancelOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(226,75,74,0.3)", cursor: "pointer", color: "#e24b4a", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" } }}>
                  <CancelIcon sx={{ fontSize: "14px" }} /> Cancel
                </Box>
              </Box>
            </RCell>
          </RRow>
        )}
      />

      {/* Record return dialog */}
      <FixedSizeDialog open={openRecord} handleClose={() => { setOpenRecord(false); setSelectedInvoice(""); }} dialogHeading="Create Supplier Return" negativeResponse="Cancel" maxWidth="sm" scroll="body"
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", p: "0.875rem 1rem", backgroundColor: "rgba(29,158,117,0.07)", border: `1px solid rgba(29,158,117,0.2)`, borderRadius: "8px" }}>
              <ReceiptIcon sx={{ fontSize: "18px", color: accent, flexShrink: 0, mt: "1px" }} />
              <Typography sx={{ fontSize: "13px", color: isDark ? "#9FE1CB" : "#0F6E56", lineHeight: 1.6 }}>Search for the purchase invoice you want to raise a return against.</Typography>
            </Box>
            <Autocomplete
              onOpen={() => fetchInvoiceList(null)}
              onChange={(_, value) => setSelectedInvoice(value ? value.id : "")}
              options={invoiceList}
              getOptionLabel={(o) => `${o.invoice_number} — ${o.related_supplier.name}`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.id}>
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{o.invoice_number}</Typography>
                    <Typography sx={{ fontSize: "11px", color: textSec }}>{o.related_supplier.name}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Purchase invoice" onChange={(e) => fetchInvoiceList(e.currentTarget.value)}
                  sx={{ "& .MuiInputBase-root": { fontSize: "13px", borderRadius: "8px" }, "& .MuiInputLabel-root": { fontSize: "13px" } }} />
              )}
            />
            <Button fullWidth disabled={!selectedInvoice} onClick={() => handleOpenReturn(selectedInvoice)}
              sx={{ fontSize: "13px", fontWeight: 500, py: "0.6rem", borderRadius: "8px", textTransform: "none", backgroundColor: accent, color: "#fff", "&:hover": { backgroundColor: "#0F6E56" }, "&:disabled": { opacity: 0.5 } }}>
              Continue to Return Form
            </Button>
          </Box>
        }
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="Return Details" negativeResponse="Close" maxWidth="lg" scroll="body"
        content={<PurchaseReturnViewForm viewData={viewData} />}
      />
      <FixedSizeDialog open={openCancel} handleClose={() => setOpenCancel(false)} dialogHeading="Cancel this return?" negativeResponse="Go back"
        positiveResponse="Yes, cancel return" handleSubmit={handleClickDelete} maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
            <CancelIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>This will cancel the supplier return and cannot be undone. Are you sure you want to proceed?</Typography>
          </Box>
        }
      />
    </Box>
  );
}